/**
 * Founder Control Panel Dashboard Service
 * Fetches data from GET /api/v1/admin/dashboard/founder
 */

import api from './api';
import type {
  FounderDashboardData,
  FounderDashboardFilters,
  FounderKPIs,
  TrendDaily,
  PaymentMixItem,
  MerchantAlert,
} from '../types/dashboardFounder';

/** Backend API response shape (slightly different from frontend types) */
interface ApiFounderDashboardResponse {
  kpis: {
    gmv: number;
    gmvDelivered?: number;
    orders: number;
    aov: number;
    platformFee: number;
    voucherCost: number;
    net: number;
    gmvChangePct?: number;
    ordersChangePct?: number;
    aovChangePct?: number;
    platformFeeChangePct?: number;
    voucherCostChangePct?: number;
    netChangePct?: number;
  };
  trendDaily: Array<{
    date: string;
    gmv: number;
    orders: number;
    cancelRate?: number | null;
    cancel_rate?: number | null;
    avgCreateToConfirmMin?: number | null;
    avgConfirmToDeliveredMin?: number | null;
    avgTotalMin?: number | null;
    newUsers?: number;
    returningUsers?: number;
  }>;
  funnel: {
    created: number;
    confirmed: number;
    delivered: number;
    cancelled: number;
  };
  paymentMix: Array<{
    paymentMethod: string;
    orders: number;
    totalAmount: number;
  }>;
  topMerchants: Array<{
    merchantId: number | string;
    name: string;
    orders: number;
    gmv: number;
    aov: number;
    cancelRate: number;
    avgPrepTimeMin: number | null;
    rating: number | null;
  }>;
  alerts: Array<{
    severity: string;
    message: string;
    merchantId: number | string;
    merchantName: string;
  }>;
}

function normalizeApiResponse(
  api: ApiFounderDashboardResponse
): FounderDashboardData {
  const changePct: FounderKPIs['changePct'] | undefined =
    api.kpis.gmvChangePct !== undefined ||
    api.kpis.ordersChangePct !== undefined ||
    api.kpis.aovChangePct !== undefined
      ? {
          gmv: api.kpis.gmvChangePct,
          orders: api.kpis.ordersChangePct,
          aov: api.kpis.aovChangePct,
          platformFee: api.kpis.platformFeeChangePct,
          voucherCost: api.kpis.voucherCostChangePct,
          net: api.kpis.netChangePct,
        }
      : undefined;

  const kpis: FounderKPIs = {
    gmv: api.kpis.gmv,
    orders: api.kpis.orders,
    aov: api.kpis.aov,
    platformFee: api.kpis.platformFee,
    voucherCost: api.kpis.voucherCost,
    net: api.kpis.net,
    changePct,
  };

  const trendDaily: TrendDaily[] = (api.trendDaily || []).map(row => {
    // Support both camelCase (cancelRate) and snake_case (cancel_rate) from API
    const cancelRate = row.cancelRate ?? row.cancel_rate ?? 0;
    return {
      date: row.date?.split('T')[0] ?? row.date,
      orders: row.orders ?? 0,
      gmv: row.gmv ?? 0,
      platformFee: 0,
      voucherCost: 0,
      net: 0,
      cancelRate:
        typeof cancelRate === 'number'
          ? cancelRate
          : parseFloat(String(cancelRate)) || 0,
      newUsers: row.newUsers ?? 0,
      returningUsers: row.returningUsers ?? 0,
      avgCreateToConfirmMin: row.avgCreateToConfirmMin ?? 0,
      avgConfirmToDeliveredMin: row.avgConfirmToDeliveredMin ?? 0,
      avgTotalMin: row.avgTotalMin ?? 0,
    };
  });

  const paymentMix: PaymentMixItem[] = (api.paymentMix || []).map(row => ({
    method: row.paymentMethod ?? 'unknown',
    count: row.orders ?? 0,
    amount: row.totalAmount ?? 0,
  }));

  const severityMap: Record<string, 'warning' | 'error' | 'info'> = {
    high: 'error',
    medium: 'warning',
    low: 'info',
  };

  const alerts: MerchantAlert[] = (api.alerts || []).map(row => ({
    merchantId: row.merchantId,
    name: row.merchantName ?? '',
    type: 'cancel_rate',
    message: row.message,
    severity: (severityMap[row.severity] ?? 'warning') as
      | 'warning'
      | 'error'
      | 'info',
    value: undefined,
  }));

  return {
    kpis,
    trendDaily,
    funnel: api.funnel ?? {
      created: 0,
      confirmed: 0,
      delivered: 0,
      cancelled: 0,
    },
    paymentMix,
    topMerchants: (api.topMerchants ?? []).map(m => ({
      ...m,
      avgPrepTimeMin: m.avgPrepTimeMin ?? 0,
      rating: m.rating ?? undefined,
    })),
    alerts,
  };
}

/**
 * Fetch founder dashboard data from API.
 */
export async function fetchFounderDashboard(
  filters: FounderDashboardFilters
): Promise<FounderDashboardData> {
  const params: Record<string, string | boolean> = {
    start: filters.startDate,
    end: filters.endDate,
    compare: String(!!filters.compareWithPrevious),
  };
  if (filters.merchantId) params.merchantId = filters.merchantId;
  if (filters.city) params.city = filters.city;
  if (filters.zone) params.zone = filters.zone;
  if (filters.paymentMethod) params.paymentMethod = filters.paymentMethod;

  try {
    const res = await api.get<{
      success?: boolean;
      data?: ApiFounderDashboardResponse;
    }>('/admin/dashboard/founder', { params });

    const raw = res.data?.data ?? res.data;
    if (!raw || typeof raw !== 'object') {
      throw new Error(
        (res.data as { message?: string })?.message ||
          'Invalid founder dashboard response'
      );
    }
    return normalizeApiResponse(raw as ApiFounderDashboardResponse);
  } catch (err: unknown) {
    const msg =
      err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { message?: string } } }).response?.data
            ?.message
        : null;
    throw new Error(
      msg ||
        (err instanceof Error ? err.message : 'Không thể tải Founder Dashboard')
    );
  }
}
